import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Text, UnstyledButton} from '@mantine/core';
import React, {useCallback, useEffect, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import navigateToURL from 'lib/navigateToURL';
import staticURL from 'lib/staticURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Container} from 'ui-library/NYC';

import {
  type AccumulatingChatHistory,
  CHAT_HISTORY_PAGE_SIZE,
  STARRED_CHAT_HISTORY_PAGE_SIZE,
  useAccumulatingChatHistory,
} from '../ChatListing';
import type {SidepanelChat} from '../ChatRouter';
import type {ChatSessionStarUpdate} from '../ChatSessionMenu';
import RedesignChatRow from './RedesignChatRow';
import SetUpAnAgentRow from './SetUpAnAgentRow';
import SidebarSectionHeader from './SidebarSectionHeader';
import useChatUnreadStates from './useChatUnreadStates';

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    // Long chat titles ellipsis within the narrow sidebar; never scroll horizontally.
    overflowX: 'hidden',
  },
  // Minimized dropdown: flow at natural height (the dropdown itself scrolls).
  rootNatural: {
    height: 'auto',
    overflowY: 'visible',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  refreshing: {
    opacity: 0.5,
  },
  viewMore: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0 4px',
    fontSize: 13,
    lineHeight: '20px',
    color: '#3C65FB',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#979799',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  emptyState: {
    boxSizing: 'border-box',
    display: 'block',
    borderRadius: theme.radius.md,
    clipPath: `inset(0 round ${theme.radius.md})`,
    margin: `0 ${theme.spacing.xs}`,
    overflow: 'hidden',
    padding: theme.spacing.md,
    textAlign: 'left',
    transition: 'background-color 150ms ease',
    '&:hover, &:focus-visible': {
      backgroundColor: theme.colors.indigo[0],
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.colors.blue[6]}`,
      outlineOffset: -2,
    },
  },
  emptyStateIllustrationContainer: {
    boxSizing: 'border-box',
    margin: `0 auto ${theme.spacing.xs}`,
    maxWidth: 480,
    padding: `0 ${theme.spacing.md}`,
    width: '100%',
  },
  emptyStateIllustration: {
    display: 'block',
    height: 'auto',
    userSelect: 'none',
    width: '100%',
  },
  emptyStateTitle: {
    color: theme.colors.dark[7],
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
  },
  emptyStateDescription: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
  },
}));

export interface GroupedChatListingProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  refetchRef: React.MutableRefObject<() => void>;
  historyActionsRef?: React.MutableRefObject<ChatHistoryActions | null>;
  /**
   * When true (sidebar), this listing scrolls. When false (minimized dropdown), it flows at its
   * natural height and the dropdown scrolls it.
   */
  fillHeight?: boolean;
  /**
   * Hide the Automated section entirely (the Agents section replaces it). Automated chats appear
   * in Recent and remain reachable via Starred and their Agent's homepage.
   */
  hideAutomatedSection?: boolean;
}

export interface ChatHistoryActions {
  handleStarUpdated: (update: ChatSessionStarUpdate) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Chat history list that independently queries and paginates the current user's starred, automated,
 * and recent chats.
 */
interface ChatHistorySectionProps {
  emptyContent?: React.ReactNode;
  history: AccumulatingChatHistory;
  renderItem: (item: AccumulatingChatHistory['items'][number]) => JSX.Element;
  showInitialLoading: boolean;
  testKey: 'starred' | 'automated' | 'recent';
  title: string;
}

function ChatHistorySection({
  emptyContent,
  history,
  renderItem,
  showInitialLoading,
  testKey,
  title,
}: ChatHistorySectionProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div
      className={classes.section}
      {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-section`})}
    >
      <div {...getTestProps({component: 'GroupedChatListing', element: `${testKey}-header`})}>
        <SidebarSectionHeader>{title}</SidebarSectionHeader>
      </div>
      <div
        aria-busy={history.isRefreshing}
        className={cx(classes.items, history.isRefreshing && classes.refreshing)}
        {...getTestProps({
          component: 'GroupedChatListing',
          element: `${testKey}-items`,
        })}
      >
        {history.isError ? (
          <Container size="sidepanel">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text={`Error loading ${title.toLowerCase()} chats.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: 'Retry',
                actionOnClick: history.retry,
                rightAlign: true,
              }}
            />
          </Container>
        ) : (
          <>
            {history.items.map((item) => renderItem(item))}
            {history.items.length === 0 && !history.isInitialLoading && emptyContent}
          </>
        )}
        {showInitialLoading && <CenteredHorizontalThrobber />}
        {(history.hasMore || history.isLoadingMore) && (
          <UnstyledButton
            className={classes.viewMore}
            disabled={history.isLoadingMore}
            onClick={() => history.loadMore?.()}
            {...getTestProps({
              component: 'GroupedChatListing',
              element: `${testKey}-view-more`,
            })}
          >
            {history.isLoadingMore ? 'Loading…' : 'View more'}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

export default function GroupedChatListing({
  selectedChatSession,
  onSelectChatSession,
  refetchRef,
  historyActionsRef,
  fillHeight = true,
  hideAutomatedSection = false,
}: GroupedChatListingProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const agentsFeatureEnabled = isAgentsFeatureEnabled();
  // The Agents section replaces the Automated section, so skip the automated query entirely when
  // it's hidden.
  const automatedSectionEnabled = agentsFeatureEnabled && !hideAutomatedSection;
  const starredHistory = useAccumulatingChatHistory({starred: true}, STARRED_CHAT_HISTORY_PAGE_SIZE);
  const automatedHistory = useAccumulatingChatHistory(
    {starred: false, automated: true},
    STARRED_CHAT_HISTORY_PAGE_SIZE,
    {enabled: automatedSectionEnabled}
  );
  const recentHistory = useAccumulatingChatHistory(
    {starred: false, automated: hideAutomatedSection ? null : false},
    CHAT_HISTORY_PAGE_SIZE
  );
  const refreshStarred = starredHistory.refresh;
  const refreshAutomated = automatedHistory.refresh;
  const refreshRecent = recentHistory.refresh;
  const removeAutomatedItem = automatedHistory.removeItem;
  const removeRecentItem = recentHistory.removeItem;
  const removeStarredItem = starredHistory.removeItem;
  const loadedChatSessionIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...starredHistory.items,
            ...(automatedSectionEnabled ? automatedHistory.items : []),
            ...recentHistory.items,
          ]
            .map(({id}) => id)
            .filter((id): id is string => id != null)
        )
      ),
    [automatedHistory.items, automatedSectionEnabled, recentHistory.items, starredHistory.items]
  );
  const {isChatUnread, onChatRealtimeUpdate} = useChatUnreadStates(
    loadedChatSessionIds,
    selectedChatSession?.id ?? null
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshStarred(), refreshAutomated(), refreshRecent()]);
  }, [refreshAutomated, refreshRecent, refreshStarred]);

  const openUseCases = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_EXPLORE_USE_CASES, {source: 'empty_chat_history'});
    navigateToURL(chatSettingsModalUrl(ChatSettingsTab.USE_CASES));
  }, []);

  const handleStarUpdated = useCallback(
    async ({chatId, isStarred}: ChatSessionStarUpdate) => {
      if (isStarred) {
        removeAutomatedItem(chatId);
        removeRecentItem(chatId);
      } else {
        removeStarredItem(chatId);
      }
      await refreshAll();
    },
    [refreshAll, removeAutomatedItem, removeRecentItem, removeStarredItem]
  );

  useEffect(() => {
    refetchRef.current = refreshAll;
    if (historyActionsRef != null) {
      historyActionsRef.current = {
        handleStarUpdated,
        refresh: refreshAll,
      };
    }
    return () => {
      if (historyActionsRef != null) {
        historyActionsRef.current = null;
      }
    };
  }, [handleStarUpdated, historyActionsRef, refetchRef, refreshAll]);

  const renderItem = useCallback(
    (item: AccumulatingChatHistory['items'][number]): JSX.Element => (
      <RedesignChatRow
        key={item.id ?? 'new-chat'}
        item={item}
        onStarUpdated={handleStarUpdated}
        onSelect={onSelectChatSession}
        onChatRealtimeUpdate={onChatRealtimeUpdate}
        isUnread={isChatUnread(item.id)}
        isSelected={selectedChatSession?.id === item.id}
      />
    ),
    [handleStarUpdated, isChatUnread, onChatRealtimeUpdate, onSelectChatSession, selectedChatSession?.id]
  );

  const showStarredSection = starredHistory.items.length > 0 || starredHistory.isError;
  const showAutomatedSection =
    automatedSectionEnabled && (!automatedHistory.isInitialLoading || automatedHistory.isError);
  const isAnyHistoryInitialLoading =
    starredHistory.isInitialLoading ||
    (automatedSectionEnabled && automatedHistory.isInitialLoading) ||
    recentHistory.isInitialLoading;
  const showRecentSection =
    recentHistory.items.length > 0 ||
    recentHistory.isError ||
    recentHistory.isRefreshing ||
    isAnyHistoryInitialLoading;
  const showEmptyState =
    !isAnyHistoryInitialLoading &&
    !starredHistory.isRefreshing &&
    (!automatedSectionEnabled || !automatedHistory.isRefreshing) &&
    !recentHistory.isRefreshing &&
    !starredHistory.isError &&
    (!automatedSectionEnabled || !automatedHistory.isError) &&
    !recentHistory.isError &&
    starredHistory.items.length === 0 &&
    (!automatedSectionEnabled || automatedHistory.items.length === 0) &&
    recentHistory.items.length === 0;

  return (
    <div
      className={cx(classes.root, fillHeight && 'scrollbar-override', !fillHeight && classes.rootNatural)}
      {...getTestProps({component: 'GroupedChatListing', element: 'root'})}
    >
      {showStarredSection && (
        <ChatHistorySection
          history={starredHistory}
          renderItem={renderItem}
          showInitialLoading={false}
          testKey="starred"
          title="Starred"
        />
      )}
      {showAutomatedSection && (
        <ChatHistorySection
          emptyContent={<SetUpAnAgentRow />}
          history={automatedHistory}
          renderItem={renderItem}
          showInitialLoading={false}
          testKey="automated"
          title="Automated"
        />
      )}
      {showRecentSection && (
        <ChatHistorySection
          history={recentHistory}
          renderItem={renderItem}
          showInitialLoading={isAnyHistoryInitialLoading}
          testKey="recent"
          title="Recent"
        />
      )}
      {showEmptyState && (
        <>
          <div {...getTestProps({component: 'GroupedChatListing', element: 'empty-header'})}>
            <SidebarSectionHeader>Get started</SidebarSectionHeader>
          </div>
          <UnstyledButton
            aria-label="Explore use cases"
            type="button"
            className={classes.emptyState}
            onClick={openUseCases}
            {...getTestProps({component: 'GroupedChatListing', element: 'empty'})}
          >
            <div
              className={classes.emptyStateIllustrationContainer}
              {...getTestProps({component: 'GroupedChatListing', element: 'empty-illustration-container'})}
            >
              <img
                alt=""
                aria-hidden="true"
                className={classes.emptyStateIllustration}
                src={staticURL('/static/img/ai-chat/sidebar-empty-state.png')}
                {...getTestProps({component: 'GroupedChatListing', element: 'empty-illustration'})}
              />
            </div>
            <Text className={classes.emptyStateTitle}>Explore use cases</Text>
            <Text className={classes.emptyStateDescription}>Get started with our library of prompts</Text>
          </UnstyledButton>
        </>
      )}
    </div>
  );
}
